import React, { useEffect, useState } from "react";
import { experience } from "../data/expirenece";
import { useGSAPReveal } from "../util/useGSAPReveal";

export const Experience: React.FC = () => {
  const [isMobileView, setIsMobileView] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(
    experience[0]?.title || ""
  );
  const [contentVisible, setContentVisible] = useState(true);

  const headerRef = useGSAPReveal({ variant: "fade-up" });
  const tabsRef = useGSAPReveal({ variant: "fade-up", delay: 0.1 });
  const detailRef = useGSAPReveal({ variant: "fade-up", delay: 0.2 });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const activeExperience = experience.find((exp) => exp.title === activeTab);

  return (
    <section
      style={{
        minHeight: "100vh",
        background: "transparent",
        padding: isMobileView
          ? "80px 24px 100px"
          : "120px 80px 140px",
        fontFamily: "'Inter', sans-serif",
        position: "relative",
        overflow: "visible",
      }}
    >
      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Section Header */}
        <div
          ref={headerRef}
          style={{
            textAlign: "center",
            marginBottom: isMobileView ? "48px" : "72px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "3px",
              background: "#C4956A",
              borderRadius: "2px",
              margin: "0 auto 24px",
            }}
          />
          <h2
            style={{
              fontSize: isMobileView ? "40px" : "56px",
              fontWeight: 600,
              fontFamily: "'Space Grotesk', sans-serif",
              color: "#F5F5F5",
              letterSpacing: "-0.02em",
              marginBottom: "16px",
            }}
          >
            Experience
          </h2>
          <p
            style={{
              fontSize: isMobileView ? "15px" : "17px",
              color: "#737373",
              fontWeight: 300,
              maxWidth: "400px",
              margin: "0 auto",
              lineHeight: 1.6,
            }}
          >
            My journey through different roles and technologies
          </p>
        </div>

        {/* Company Tabs — Underline Style */}
        <div
          ref={tabsRef}
          style={{
            marginBottom: "48px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "4px",
              overflowX: "auto",
              overflowY: "visible",
              scrollBehavior: "smooth",
              paddingBottom: "2px",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              justifyContent: isMobileView ? "flex-start" : "center",
              borderBottom: "1px solid rgba(245, 245, 245, 0.06)",
            }}
          >
            {experience.map((exp) => (
              <button
                key={exp.title}
                onClick={() => {
                  if (activeTab !== exp.title) {
                    setContentVisible(false);
                    setTimeout(() => {
                      setActiveTab(exp.title);
                      setContentVisible(true);
                    }, 200);
                  }
                }}
                style={{
                  minWidth: "fit-content",
                  padding: "12px 20px",
                  backgroundColor: "transparent",
                  border: "none",
                  borderBottom: activeTab === exp.title
                    ? "2px solid #C4956A"
                    : "2px solid transparent",
                  color: activeTab === exp.title
                    ? "#F5F5F5"
                    : "#737373",
                  fontSize: "14px",
                  fontWeight: activeTab === exp.title ? 500 : 400,
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                  fontFamily: "'Inter', sans-serif",
                  whiteSpace: "nowrap",
                  letterSpacing: "0.01em",
                  marginBottom: "-1px",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== exp.title) {
                    e.currentTarget.style.color = "#A3A3A3";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== exp.title) {
                    e.currentTarget.style.color = "#737373";
                  }
                }}
              >
                {exp.title}
              </button>
            ))}
          </div>
        </div>

        {/* Experience Details */}
        <div
          ref={detailRef}
        >
          {activeExperience && (
            <div
              style={{
                backgroundColor: "#111111",
                border: "1px solid rgba(245, 245, 245, 0.08)",
                borderRadius: "24px",
                padding: isMobileView ? "28px" : "48px",
                position: "relative",
                overflow: "hidden",
                opacity: contentVisible ? 1 : 0,
                transform: contentVisible
                  ? "translateY(0)"
                  : "translateY(10px)",
                transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                maxWidth: "900px",
                margin: "0 auto",
              }}
            >
              {/* Role and Company */}
              <div style={{ marginBottom: "28px" }}>
                <h3
                  style={{
                    fontSize: isMobileView ? "24px" : "30px",
                    fontWeight: 600,
                    fontFamily: "'Space Grotesk', sans-serif",
                    color: "#F5F5F5",
                    marginBottom: "12px",
                    lineHeight: "1.2",
                  }}
                >
                  {activeExperience.content.workingAsA}
                </h3>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    flexWrap: "wrap",
                    marginBottom: "8px",
                  }}
                >
                  {activeExperience.content.website ? (
                    <a
                      href={activeExperience.content.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: "15px",
                        fontWeight: 500,
                        color: "#C4956A",
                        textDecoration: "none",
                        transition: "opacity 0.2s ease",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = "0.7";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = "1";
                      }}
                    >
                      @{activeExperience.title}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" />
                      </svg>
                    </a>
                  ) : (
                    <span
                      style={{
                        fontSize: "15px",
                        fontWeight: 500,
                        color: "#C4956A",
                      }}
                    >
                      @{activeExperience.title}
                    </span>
                  )}

                  <span style={{ color: '#525252' }}>|</span>

                  <span
                    style={{
                      fontSize: "14px",
                      color: "#737373",
                      fontWeight: 400,
                    }}
                  >
                    {activeExperience.content.workDuration}
                  </span>
                </div>
              </div>

              {/* Responsibilities */}
              <div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}
                >
                  {activeExperience.content.learned.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "14px",
                      }}
                    >
                      <div
                        style={{
                          width: "4px",
                          height: "4px",
                          borderRadius: "1px",
                          backgroundColor: "#C4956A",
                          opacity: 0.6,
                          marginTop: "10px",
                          flexShrink: 0,
                        }}
                      />
                      <p
                        style={{
                          fontSize: "15px",
                          lineHeight: "1.7",
                          color: "#A3A3A3",
                          margin: 0,
                          fontWeight: 300,
                        }}
                      >
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};
